<template>
  <Button
    v-if="actionType === 'add'"
    icon="pi pi-plus"
    class="p-button p-button-outlined mr-4 mt-3"
    label="افزودن آدرس جدید"
    @click="toggleModal"
  />
  <Button v-else class="p-button-icon-only p-button-outlined badge-button mr-2" @click="toggleModal">
    <i class="pi pi-pencil"></i>
  </Button>

  <Dialog
    v-model:visible="visible"
    modal
    class="customer-address-dialog"
    :style="{ width: 'min(760px, calc(100vw - 2rem))' }"
    :show-header="false"
    :closable="!saving"
    :close-on-escape="!saving"
  >
    <form class="address-dialog" dir="rtl" @submit.prevent="saveData">
      <header class="address-dialog-header">
        <div class="address-dialog-title-row">
          <span class="address-dialog-icon" aria-hidden="true">
            <i :class="actionType === 'add' ? 'pi pi-plus' : 'pi pi-pencil'"></i>
          </span>
          <div>
            <span class="address-dialog-kicker">مدیریت آدرس</span>
            <h2>{{ actionType === 'add' ? 'افزودن آدرس جدید' : 'ویرایش آدرس' }}</h2>
            <p>
              {{
                actionType === 'add'
                  ? 'اطلاعات آدرس را وارد کنید تا در سفارش استفاده شود.'
                  : 'اطلاعات آدرس را بررسی و تغییرات موردنظر را ذخیره کنید.'
              }}
            </p>
          </div>
        </div>
        <button
          type="button"
          class="address-dialog-close"
          aria-label="بستن"
          :disabled="saving"
          @click="visible = false"
        >
          <i class="pi pi-times" aria-hidden="true"></i>
        </button>
      </header>

      <div class="address-dialog-body">
        <section class="address-dialog-section">
          <div class="address-section-heading">
            <div>
              <span class="address-section-kicker">جزئیات اصلی</span>
              <h3>نشانی کامل</h3>
            </div>
            <span class="address-required-badge">ضروری</span>
          </div>
          <div class="address-dialog-field address-dialog-field--wide">
            <label for="address">آدرس</label>
            <Textarea
              v-model="currentData.address"
              id="address"
              rows="3"
              auto-resize
              class="w-full"
              autocomplete="off"
              placeholder="نشانی کامل را وارد نمایید"
            />
          </div>
        </section>

        <section class="address-dialog-section">
          <div class="address-section-heading">
            <div>
              <span class="address-section-kicker">موقعیت</span>
              <h3>اطلاعات جغرافیایی</h3>
            </div>
            <i class="pi pi-map-marker address-section-symbol" aria-hidden="true"></i>
          </div>
          <div class="address-dialog-grid">
            <div class="address-dialog-field">
              <label for="address_country_id">کشور</label>
              <SelectInput
                v-model="currentData.country_id"
                id="address_country_id"
                placeholder="انتخاب کشور ..."
                type-primary="Country"
                styles="w-full"
              />
            </div>
            <div class="address-dialog-field">
              <label for="address_city_id">شهر</label>
              <SelectInput
                v-model="currentData.city_id"
                id="address_city_id"
                placeholder="انتخاب شهر ..."
                :id-primary="currentData.country_id"
                :disabled="!currentData.country_id"
                type-primary="City"
                styles="w-full"
              />
            </div>
            <div class="address-dialog-field">
              <label for="address_section_id">منطقه</label>
              <SelectInput
                v-model="currentData.section_id"
                id="address_section_id"
                placeholder="منطقه ..."
                :id-primary="currentData.city_id"
                :disabled="!currentData.city_id"
                type-primary="SectionList"
                styles="w-full"
              />
            </div>
            <div class="address-dialog-field">
              <label for="postal_code">کد پستی</label>
              <InputText
                v-model="currentData.postal_code"
                id="postal_code"
                class="w-full"
                placeholder="کد پستی را وارد نمایید"
              />
            </div>
          </div>
        </section>
      </div>

      <footer class="address-dialog-footer">
        <span class="address-dialog-tip">
          <i class="pi pi-info-circle" aria-hidden="true"></i>
          برای انتخاب شهر و منطقه، ابتدا کشور و شهر را انتخاب کنید.
        </span>
        <div class="address-dialog-actions">
          <Button type="button" label="انصراف" severity="secondary" outlined :disabled="saving" @click="visible = false" />
          <Button
            type="submit"
            :label="actionType === 'add' ? 'افزودن آدرس' : 'ذخیره تغییرات'"
            icon="pi pi-check"
            :loading="saving"
            :disabled="saving"
          />
        </div>
      </footer>
    </form>
  </Dialog>
</template>

<script setup>
import { ref } from 'vue';
import { CustomerService } from '@/service/CustomerService';
import emitter from '@/utils/eventBus';
import SelectInput from '@/components/Global/SelectInput.vue';
const CustomerServiceItem = ref(new CustomerService());
const currentData = ref({});
const visible = ref(false);
const saving = ref(false);
const props = defineProps({
  prevData: {
    default: () => {
      return {};
    },
    type: Object,
    required: false
  },
  actionType: {
    type: String,
    required: true
  },
  customerId: {
    type: Number,
    required: true
  },
  isInStaff: {
    type: Boolean,
    required: false,
    default: false
  }
});

const saveData = async () => {
  if (saving.value) return;

  saving.value = true;
  currentData.value.customer_id = props.customerId;

  try {
    if (props.actionType === 'add') {
      await CustomerServiceItem.value.createCustomerAddress(currentData.value);
      emitter.emit('toast', { type: 'success', message: 'آدرس با موفقیت ایجاد شد' });
    } else {
      await CustomerServiceItem.value.editCustomerAddress(props.prevData.id, currentData.value);
      emitter.emit('toast', { type: 'success', message: 'آدرس با موفقیت بروزرسانی شد' });
    }

    visible.value = false;
    emitter.emit(props.isInStaff ? 'refresh-table-for-addresses' : 'refresh-complete-information');
  } catch (error) {
    emitter.emit('toast-axios', error);
  } finally {
    saving.value = false;
  }
};
const toggleModal = () => {
  visible.value = !visible.value;
  if (props.actionType === 'edit') {
    currentData.value = {
      address: props.prevData.address,
      postal_code: props.prevData.postal_code,
      city_id: props.prevData.city_id,
      section_id: props.prevData.section_id,
      country_id: props.prevData.city.country.id,
      customer_id: props.customerId
    };
  } else {
    currentData.value = {};
  }
};
defineExpose({
  toggleModal
});
</script>
<style scoped lang="scss">
.customer-address-dialog {
  overflow: hidden;
  border: 1px solid color-mix(in srgb, var(--fm-primary) 16%, var(--fm-border));
  border-radius: 1.35rem;
  background: var(--fm-surface);
  box-shadow: 0 24px 80px rgb(15 23 42 / 20%);
}

.customer-address-dialog :deep(.p-dialog-content) {
  padding: 0;
  background: transparent;
}

.address-dialog {
  color: var(--fm-text-strong);
}

.address-dialog-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.35rem 1.5rem 1.15rem;
  border-bottom: 1px solid var(--fm-border);
  background: linear-gradient(
    135deg,
    color-mix(in srgb, var(--fm-primary) 8%, var(--fm-surface)),
    var(--fm-surface) 72%
  );
}

.address-dialog-title-row {
  display: flex;
  align-items: flex-start;
  min-width: 0;
  gap: 0.9rem;
}

.address-dialog-icon {
  display: grid;
  flex: 0 0 auto;
  width: 3.3rem;
  height: 3.3rem;
  border: 6px solid color-mix(in srgb, var(--fm-primary) 12%, var(--fm-surface));
  border-radius: 1.1rem;
  color: #fff;
  background: linear-gradient(145deg, color-mix(in srgb, var(--fm-primary) 72%, #fff), var(--fm-primary));
  box-shadow: 0 9px 20px color-mix(in srgb, var(--fm-primary) 20%, transparent);
  place-items: center;
}

.address-dialog-icon i {
  font-size: 1.15rem;
}

.address-dialog-kicker,
.address-section-kicker {
  display: block;
  color: var(--fm-primary);
  font-size: 0.76rem;
  font-weight: 800;
}

.address-dialog-kicker {
  margin-bottom: 0.25rem;
}

.address-dialog-header h2,
.address-section-heading h3 {
  margin: 0;
  color: var(--fm-text-strong);
  font-weight: 800;
}

.address-dialog-header h2 {
  font-size: 1.25rem;
  line-height: 1.4;
}

.address-dialog-header p {
  max-width: 38rem;
  margin: 0.35rem 0 0;
  color: var(--fm-text-muted);
  font-size: 0.84rem;
  line-height: 1.8;
}

.address-dialog-close {
  display: inline-grid;
  flex: 0 0 auto;
  width: 2.25rem;
  height: 2.25rem;
  padding: 0;
  border: 1px solid transparent;
  border-radius: 0.75rem;
  color: var(--fm-text-muted);
  background: transparent;
  cursor: pointer;
  place-items: center;
  transition: color 0.15s ease, background-color 0.15s ease, border-color 0.15s ease;
}

.address-dialog-close:hover {
  border-color: var(--fm-border);
  color: var(--fm-text-strong);
  background: var(--fm-surface-muted);
}

.address-dialog-close:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.address-dialog-close:focus-visible {
  outline: 3px solid var(--fm-focus);
  outline-offset: 2px;
}

.address-dialog-body {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1.25rem 1.5rem 0;
}

.address-dialog-section {
  padding: 1rem;
  border: 1px solid var(--fm-border);
  border-radius: var(--fm-radius-lg);
  background: color-mix(in srgb, var(--fm-primary) 2%, var(--fm-surface));
}

.address-section-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
}

.address-section-heading h3 {
  margin-top: 0.18rem;
  font-size: 0.98rem;
}

.address-required-badge {
  padding: 0.25rem 0.55rem;
  border: 1px solid color-mix(in srgb, var(--fm-warning) 24%, var(--fm-border));
  border-radius: 999px;
  color: var(--fm-warning);
  background: color-mix(in srgb, var(--fm-warning) 8%, var(--fm-surface));
  font-size: 0.72rem;
  font-weight: 800;
  white-space: nowrap;
}

.address-section-symbol {
  color: var(--fm-primary);
  font-size: 1.05rem;
}

.address-dialog-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
}

.address-dialog-field {
  min-width: 0;
}

.address-dialog-field--wide {
  width: 100%;
}

.address-dialog-field label {
  display: block;
  margin-bottom: 0.45rem;
  color: var(--fm-text-muted);
  font-size: 0.8rem;
  font-weight: 800;
}

.address-dialog-field :deep(.p-inputtext),
.address-dialog-field :deep(.p-inputtextarea),
.address-dialog-field :deep(.p-dropdown) {
  width: 100%;
}

.address-dialog-field :deep(.p-inputtextarea) {
  min-height: 5.5rem;
  line-height: 1.8;
  resize: vertical;
}

.address-dialog-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.15rem 1.5rem 1.5rem;
}

.address-dialog-tip {
  display: inline-flex;
  align-items: center;
  min-width: 0;
  gap: 0.45rem;
  color: var(--fm-text-muted);
  font-size: 0.76rem;
  line-height: 1.6;
}

.address-dialog-tip i {
  flex: 0 0 auto;
  color: var(--fm-primary);
}

.address-dialog-actions {
  display: flex;
  flex: 0 0 auto;
  gap: 0.75rem;
}

.address-dialog-actions .p-button {
  min-height: 2.75rem;
}

@media (max-width: 640px) {
  .address-dialog-header,
  .address-dialog-body,
  .address-dialog-footer {
    padding-inline: 1rem;
  }

  .address-dialog-header {
    padding-top: 1.1rem;
  }

  .address-dialog-title-row {
    gap: 0.7rem;
  }

  .address-dialog-icon {
    width: 2.9rem;
    height: 2.9rem;
    border-width: 5px;
    border-radius: 0.95rem;
  }

  .address-dialog-header h2 {
    font-size: 1.08rem;
  }

  .address-dialog-header p {
    font-size: 0.78rem;
  }

  .address-dialog-grid {
    grid-template-columns: 1fr;
  }

  .address-dialog-footer {
    align-items: stretch;
    flex-direction: column;
  }

  .address-dialog-actions {
    width: 100%;
  }

  .address-dialog-actions .p-button {
    flex: 1 1 0;
  }
}

.badge-button {
  vertical-align: middle;
  height: 26px !important;
  width: 26px !important;
}
</style>
